<template>
  <n-card :class="{ 'base-module--disabled': inactive }">
    <n-grid
      :cols="6"
      x-gap="24"
      y-gap="24"
      item-responsive
      responsive="screen"
    >
      <n-grid-item :span="6">
        <n-space
          align="center"
          justify="space-between"
          :size="16"
        >
          <n-space
            align="center"
            :size="16"
          >
            <div
              :class="[
                'base-module__status',
                { 'base-module__status--inactive': !installed },
                { 'base-module__status--connected': connected && !connecting },
                { 'base-module__status--connecting': connecting },
                { 'base-module__status--error': error },
                { 'base-module__status--active': isActive },
              ]"
            ></div>
            <h4 class="base-module__title">
              <slot name="title"></slot>
            </h4>
          </n-space>
          <slot
            v-if="!connecting"
            name="config"
          ></slot>
        </n-space>
      </n-grid-item>

      <n-grid-item
        v-if="slots.left && listening"
        span="3 s:2"
        :class="{ 'base-module--disabled': !contentActive }"
      >
        <slot name="left"></slot>
      </n-grid-item>

      <n-grid-item
        v-if="slots.right && listening"
        span="3 s:4"
        :class="{ 'base-module--disabled': !contentActive }"
      >
        <slot name="right"></slot>
      </n-grid-item>

      <n-grid-item
        v-if="slots.middle && listening"
        :span="6"
        :class="{ 'base-module--disabled': !contentActive }"
      >
        <slot name="middle"></slot>
      </n-grid-item>

      <n-grid-item
        v-if="slots.bottom && listening"
        :span="6"
        :class="{ 'base-module--disabled': !contentActive }"
      >
        <slot name="bottom"></slot>
      </n-grid-item>

      <n-grid-item
        v-if="!listening && installed"
        :span="6"
      >
        <p>
          {{ t('modules.notFound') }}
        </p>
      </n-grid-item>

      <n-grid-item
        v-if="!listening && installed"
        :span="6"
      >
        <BfButton @click="subscribeToTopics">
          {{ t('common.connection.reconnect') }}
        </BfButton>
      </n-grid-item>

      <n-grid-item
        v-if="!installed"
        :span="6"
        :class="{ 'base-module--disabled': !contentActive }"
      >
        <n-alert
          :title="t('modules.notAvailable.index')"
          :bordered="false"
        >
          <template #icon>
            <Icon
              icon="mdi:information-outline"
              :style="{ fontSize: '24px' }"
            />
          </template>
          <p>
            {{ t('modules.notAvailable.content') }}
          </p>
        </n-alert>
      </n-grid-item>
    </n-grid>
  </n-card>
</template>

<script setup lang="ts">
import { NCard, NSpace, NGrid, NGridItem, NAlert } from 'naive-ui'
import { BfButton } from '@finetic/bikefitting-ui'
import { Icon } from '@iconify/vue'

import { ref, onMounted, onUnmounted, useSlots, watch, computed } from 'vue'
import { MqttValue, MqttContainer } from '@/mqttDefinitions'
import { useMqttStore } from '@/store/mqttStore'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { arrayEquals } from '@/utils/utils'

const { t } = useI18n()

const mqttStore = useMqttStore()
const slots = useSlots()

const { doSubscribe, doUnSubscribe, getTopicsFromDefinition } = mqttStore
const { client } = storeToRefs(mqttStore)

const topics = ref([] as string[])

let timeoutId: ReturnType<typeof setTimeout>

const moduleMessages = computed(() => {
  return mqttStore.getMessagesOnTopics(topics.value)
})

const props = withDefaults(
  defineProps<{
    installed?: boolean
    inactive?: boolean
    connected?: boolean
    connecting?: boolean
    error?: boolean
    contentActive?: boolean
    useActive?: boolean
    definition?: MqttValue | MqttContainer
  }>(),
  {
    installed: true,
    inactive: false,
    connected: false,
    connecting: false,
    error: false,
    contentActive: true,
    useActive: false,
    definition: undefined,
  },
)

const listening = ref(false)
const isActive = ref(false)

const subscribeToTopics = () => {
  topics.value.forEach(async (topic) => {
    listening.value = await doSubscribe(topic, client.value)
  })
}

onMounted(() => {
  if (props.definition && props.installed) {
    topics.value = getTopicsFromDefinition(props.definition)
    subscribeToTopics()
  }
})

onUnmounted(() => {
  topics.value.forEach((topic) => {
    doUnSubscribe(topic, client.value)
  })
})

watch(moduleMessages, (newMessages, oldMessages) => {
  if (!arrayEquals(newMessages, oldMessages) && props.useActive) {
    isActive.value = true
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      isActive.value = false
    }, 60000)
  }
})
</script>

<style lang="scss" scoped>
.base-module {
  &--disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  &__title {
    margin-bottom: 0;
  }

  &__status {
    height: 10px;
    width: 10px;
    background: #0e0e0e;
    border-radius: 50%;

    &--connecting {
      background: var(--color-primary);
      box-shadow: none;
      animation: blink 0.5s infinite alternate;
    }

    &--connected {
      background: #00ff57;
      box-shadow: none;
    }

    &--active {
      background: #00ff57;
      box-shadow:
        0 0 4px #00ff57,
        0 0 10px #00ff57;
    }

    &--inactive {
      background: #0e0e0e;
      box-shadow: none;
    }

    &--error {
      background: #ff0000;
      box-shadow: 0 0 10px #ff0000;
    }
  }
}

@keyframes blink {
  0% {
    background: var(--color-primary);
  }
  100% {
    background: black;
  }
}

.n-form-item.n-form-item--left-labelled {
  grid-template-columns: auto minmax(0, 1fr);

  .n-form-item-label {
    max-width: 100px;
  }
}
</style>
